import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthService } from '../../auth/auth.service';
import { CommonService } from '../common.service';
import { Store } from '../common.enums';
import { RealtimeStartupCoordinatorService } from '../collaboration/realtime-startup-coordinator.service';

export type BootState = 'idle' | 'booting' | 'warming' | 'ready' | 'error';
export type BootTaskPhase = 'critical' | 'warmup' | 'background';
export type BootTaskStatus = 'pending' | 'running' | 'retrying' | 'success' | 'error' | 'cancelled';

export interface BootTaskSnapshot {
  readonly id: string;
  readonly label: string;
  readonly phase: BootTaskPhase;
  readonly weight: number;
  readonly timeoutMs: number;
  readonly retries: number;
  readonly attempts: number;
  readonly status: BootTaskStatus;
  readonly result: unknown | null;
  readonly error: unknown | null;
  readonly startedAt: number | null;
  readonly finishedAt: number | null;
}

export interface BootSnapshot {
  readonly state: BootState;
  readonly progress: number;
  readonly phaseLabel: string;
  readonly sessionGeneration: number;
  readonly activeUserId: number | null;
  readonly tasks: readonly BootTaskSnapshot[];
  readonly error: unknown | null;
}

export interface BootTimingSnapshot {
  readonly mode: 'cold' | 'warm';
  readonly networkType: string;
  readonly startedAt: number;
  readonly shellVisibleMs: number | null;
  readonly firstRouteUsableMs: number | null;
  readonly readyMs: number | null;
}

export interface BootMetricsSnapshot {
  readonly latest: BootTimingSnapshot | null;
  readonly history: readonly BootTimingSnapshot[];
}

export interface BootTaskDefinition {
  readonly id: string;
  readonly label: string;
  readonly phase: BootTaskPhase;
  readonly weight: number;
  readonly timeoutMs: number;
  readonly retries: number;
  readonly run: () => Promise<unknown> | unknown;
}

interface MutableBootTask extends BootTaskSnapshot {
  attempts: number;
  status: BootTaskStatus;
  result: unknown | null;
  error: unknown | null;
  startedAt: number | null;
  finishedAt: number | null;
}

class StaleBootRunError extends Error {
  constructor() {
    super('El contexto de boot ya no es vigente');
  }
}

class BootTaskFailure extends Error {
  constructor(
    readonly taskId: string,
    readonly cause: unknown,
  ) {
    super(`Falló la tarea de boot ${taskId}`);
  }
}

const CLOSED_TASK_STATUSES: readonly BootTaskStatus[] = ['success', 'error', 'cancelled'];

/** Calculates progress only from closed weighted critical/warm-up tasks. */
export function calculateBootProgress(tasks: readonly Pick<BootTaskSnapshot, 'phase' | 'weight' | 'status'>[]): number {
  const visibleTasks = tasks.filter((task) => task.phase !== 'background' && task.weight > 0);
  const totalWeight = visibleTasks.reduce((total, task) => total + task.weight, 0);
  if (totalWeight === 0) {
    return 100;
  }

  const closedWeight = visibleTasks.reduce(
    (total, task) => CLOSED_TASK_STATUSES.includes(task.status) ? total + task.weight : total,
    0,
  );
  return Math.max(0, Math.min(100, Math.floor((closedWeight / totalWeight) * 100)));
}

@Injectable({ providedIn: 'root' })
export class BootCoordinatorService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly shellVisibleSubject = new BehaviorSubject<boolean>(false);
  private readonly firstRouteReadySubject = new BehaviorSubject<boolean>(false);
  private readonly snapshotSubject = new BehaviorSubject<BootSnapshot>({
    state: 'idle',
    progress: 0,
    phaseLabel: 'Iniciando aplicación',
    sessionGeneration: 0,
    activeUserId: null,
    tasks: [],
    error: null,
  });
  private readonly metricsSubject = new BehaviorSubject<BootMetricsSnapshot>({
    latest: null,
    history: [],
  });

  private started = false;
  private starting = false;
  private restoringSession = false;
  private contextRunning = false;
  private sessionGeneration = 0;
  private activeUserId: number | null = null;
  private activeStore: Store = Store.hds;
  private shellVisible = false;
  private firstRouteReady = false;
  private currentTasks: MutableBootTask[] = [];
  private hasCompletedContext = false;
  private timingGeneration: number | null = null;
  private timing: BootTimingSnapshot | null = null;

  readonly snapshot$ = this.snapshotSubject.asObservable();
  readonly metrics$ = this.metricsSubject.asObservable();
  readonly state$ = this.snapshot$.pipe(
    map((snapshot) => snapshot.state),
    distinctUntilChanged(),
  );

  getMetrics(): BootMetricsSnapshot {
    const metrics = this.metricsSubject.value;
    return { latest: metrics.latest, history: [...metrics.history] };
  }

  constructor(
    private readonly authService: AuthService,
    private readonly commonService: CommonService,
    private readonly router: Router,
    private readonly realtimeCoordinator: RealtimeStartupCoordinatorService,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.starting = true;
    this.activeStore = this.commonService.currentStore$.value;

    this.authService.user
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        const userId = Number(user?.id || 0) || null;
        if (this.starting) {
          this.activeUserId = userId;
          return;
        }
        if (this.restoringSession) {
          if (this.activeUserId === null || userId === this.activeUserId) {
            this.activeUserId = userId;
            return;
          }
          this.startContext(userId, false);
          return;
        }
        if (this.contextRunning && userId === this.activeUserId) {
          return;
        }
        this.startContext(userId, false);
      });

    this.commonService.currentStore$
      .pipe(takeUntil(this.destroy$))
      .subscribe((store) => {
        if (this.activeStore === store) {
          return;
        }
        this.activeStore = store;
        if (this.isBlocking() && this.activeUserId !== null) {
          this.cancelForContextChange();
          this.startContext(this.activeUserId, false);
        }
      });

    this.starting = false;
    this.startContext(Number(this.authService.user.value?.id || 0) || null, false);
  }

  markShellVisible(): void {
    if (this.shellVisible) {
      return;
    }
    this.shellVisible = true;
    this.shellVisibleSubject.next(true);
  }

  markFirstAuthorizedRouteReady(): void {
    if (!this.authService.loggedIn || this.firstRouteReady) {
      return;
    }
    this.firstRouteReady = true;
    this.firstRouteReadySubject.next(true);
  }

  retry(): void {
    if (this.snapshotSubject.value.state !== 'error') {
      return;
    }
    this.startContext(Number(this.authService.user.value?.id || 0) || null, true);
  }

  /** Called by a real global store switcher after it changes currentStore$. */
  restartForStoreContext(): void {
    if (this.activeUserId === null) {
      return;
    }
    this.activeStore = this.commonService.currentStore$.value;
    this.cancelForContextChange();
    this.startContext(this.activeUserId, false);
  }

  ngOnDestroy(): void {
    this.cancelForContextChange();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startContext(userId: number | null, preserveCompletedTasks: boolean): void {
    const generation = ++this.sessionGeneration;
    this.activeUserId = userId;
    this.contextRunning = true;
    this.timingGeneration = generation;
    this.timing = {
      mode: this.hasCompletedContext ? 'warm' : 'cold',
      networkType: this.getNetworkType(),
      startedAt: Date.now(),
      shellVisibleMs: null,
      firstRouteUsableMs: null,
      readyMs: null,
    };
    this.firstRouteReady = !!userId && this.router.url !== '/login' && this.router.url !== '/';
    this.firstRouteReadySubject.next(this.firstRouteReady);

    const definitions = this.createTaskDefinitions();
    const previousTasks = preserveCompletedTasks ? this.currentTasks : [];
    const tasks = definitions.map((definition) => this.createTask(definition, previousTasks));
    this.currentTasks = tasks;
    this.publishSnapshot('booting', tasks, generation, null);
    void this.runContext(definitions, tasks, generation, userId);
  }

  private async runContext(
    definitions: BootTaskDefinition[],
    tasks: MutableBootTask[],
    generation: number,
    initialUserId: number | null,
  ): Promise<void> {
    this.restoringSession = true;
    try {
      await this.executeTask(definitions[0], tasks, generation);
      this.restoringSession = false;
      this.activeUserId = Number(this.authService.user.value?.id || 0) || initialUserId;

      await this.executeTask(definitions[1], tasks, generation);
      await this.executeTask(definitions[2], tasks, generation);
      await this.executeTask(definitions[3], tasks, generation);
      this.recordTiming(generation, 'shellVisibleMs');

      this.publishSnapshot('warming', tasks, generation, null);
      await this.executeTask(definitions[4], tasks, generation);
      if (this.authService.loggedIn) {
        this.recordTiming(generation, 'firstRouteUsableMs');
      }

      this.recordTiming(generation, 'readyMs');
      this.hasCompletedContext = true;
      this.publishSnapshot('ready', tasks, generation, null);
      void this.executeTask(definitions[5], tasks, generation).catch(() => undefined);
    } catch (error) {
      if (generation === this.sessionGeneration) {
        this.restoringSession = false;
      }
      if (error instanceof StaleBootRunError || generation !== this.sessionGeneration) {
        return;
      }
      this.publishSnapshot('error', tasks, generation, error instanceof BootTaskFailure ? error.cause : error);
    } finally {
      if (generation === this.sessionGeneration) {
        this.contextRunning = false;
      }
    }
  }

  private createTaskDefinitions(): BootTaskDefinition[] {
    return [
      {
        id: 'restore-session',
        label: 'Restaurando sesión',
        phase: 'critical',
        weight: 3,
        timeoutMs: 7000,
        retries: 1,
        run: () => this.authService.restoreSession(),
      },
      {
        id: 'resolve-user-permissions',
        label: 'Resolviendo usuario y permisos',
        phase: 'critical',
        weight: 3,
        timeoutMs: 7000,
        retries: 1,
        run: async () => {
          const user = this.authService.user.value;
          if (!user) {
            return { skipped: true, reason: 'no-session' };
          }
          const updatedUser = await this.authService.refreshUserAccess(user.id, false);
          return { userId: user.id, permissionCount: updatedUser?.access.length ?? 0 };
        },
      },
      {
        id: 'resolve-store-context',
        label: 'Preparando tienda actual',
        phase: 'critical',
        weight: 1,
        timeoutMs: 1000,
        retries: 0,
        run: () => {
          const store = this.commonService.currentStore$.value;
          if (!Object.values(Store).includes(store)) {
            throw new Error('La tienda actual no es válida');
          }
          return { store };
        },
      },
      {
        id: 'shell-visible',
        label: 'Mostrando aplicación',
        phase: 'critical',
        weight: 2,
        timeoutMs: 7000,
        retries: 0,
        run: () => this.shellVisible
          ? true
          : firstValueFrom(this.shellVisibleSubject.pipe(filter(Boolean), take(1))),
      },
      {
        id: 'first-authorized-route',
        label: 'Preparando primera ruta autorizada',
        phase: 'critical',
        weight: 3,
        timeoutMs: 10000,
        retries: 1,
        run: () => !this.authService.loggedIn || this.firstRouteReady
          ? { skipped: !this.authService.loggedIn }
          : firstValueFrom(this.firstRouteReadySubject.pipe(filter(Boolean), take(1))),
      },
      {
        id: 'realtime-background',
        label: 'Activando colaboración en segundo plano',
        phase: 'background',
        weight: 1,
        timeoutMs: 5000,
        retries: 1,
        run: () => {
          this.realtimeCoordinator.start();
          return { started: true };
        },
      },
    ];
  }

  private createTask(definition: BootTaskDefinition, previousTasks: MutableBootTask[]): MutableBootTask {
    const previous = previousTasks.find((task) => task.id === definition.id);
    if (previous?.status === 'success') {
      return { ...previous };
    }
    return {
      id: definition.id,
      label: definition.label,
      phase: definition.phase,
      weight: definition.weight,
      timeoutMs: definition.timeoutMs,
      retries: definition.retries,
      attempts: 0,
      status: 'pending',
      result: null,
      error: null,
      startedAt: null,
      finishedAt: null,
    };
  }

  private async executeTask(
    definition: BootTaskDefinition,
    tasks: MutableBootTask[],
    generation: number,
  ): Promise<unknown> {
    const task = tasks.find((candidate) => candidate.id === definition.id);
    if (!task) {
      throw new Error(`Tarea de boot inexistente: ${definition.id}`);
    }
    if (task.status === 'success') {
      return task.result;
    }

    for (let attempt = 1; attempt <= definition.retries + 1; attempt += 1) {
      this.assertCurrent(generation);
      task.attempts = attempt;
      task.status = attempt === 1 ? 'running' : 'retrying';
      task.error = null;
      task.startedAt = task.startedAt ?? Date.now();
      this.publishSnapshot(this.snapshotSubject.value.state, tasks, generation, null);

      try {
        const result = await this.withTimeout(
          Promise.resolve().then(() => definition.run()),
          definition.timeoutMs,
        );
        this.assertCurrent(generation);
        task.status = 'success';
        task.result = result;
        task.finishedAt = Date.now();
        this.publishSnapshot(this.snapshotSubject.value.state, tasks, generation, null);
        return result;
      } catch (error) {
        if (error instanceof StaleBootRunError || generation !== this.sessionGeneration) {
          task.status = 'cancelled';
          task.error = error;
          task.finishedAt = Date.now();
          throw new StaleBootRunError();
        }
        task.error = error;
        if (attempt <= definition.retries) {
          task.status = 'retrying';
          this.publishSnapshot(this.snapshotSubject.value.state, tasks, generation, null);
          continue;
        }
        task.status = 'error';
        task.finishedAt = Date.now();
        this.publishSnapshot(this.snapshotSubject.value.state, tasks, generation, error);
        throw new BootTaskFailure(definition.id, error);
      }
    }

    throw new Error(`No se pudo ejecutar la tarea ${definition.id}`);
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => reject(new Error('timeout')), timeoutMs);
      promise.then(
        (value) => {
          clearTimeout(timeoutId);
          resolve(value);
        },
        (error: unknown) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      );
    });
  }

  private recordTiming(
    generation: number,
    point: 'shellVisibleMs' | 'firstRouteUsableMs' | 'readyMs',
  ): void {
    if (generation !== this.sessionGeneration || this.timingGeneration !== generation || !this.timing) {
      return;
    }
    const elapsedMs = Math.max(0, Date.now() - this.timing.startedAt);
    this.timing = { ...this.timing, [point]: elapsedMs };
    if (point === 'readyMs') {
      const metrics = this.metricsSubject.value;
      this.metricsSubject.next({
        latest: this.timing,
        history: [...metrics.history, this.timing].slice(-20),
      });
    } else {
      this.metricsSubject.next({
        latest: this.timing,
        history: this.metricsSubject.value.history,
      });
    }
  }

  private getNetworkType(): string {
    if (typeof navigator === 'undefined') {
      return 'unknown';
    }
    const connection = (navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }).connection;
    const effectiveType = connection?.effectiveType ?? 'unknown';
    return connection?.saveData ? `${effectiveType};saveData` : effectiveType;
  }

  private assertCurrent(generation: number): void {
    if (generation !== this.sessionGeneration) {
      throw new StaleBootRunError();
    }
  }

  private cancelForContextChange(): void {
    this.sessionGeneration += 1;
    this.restoringSession = false;
    this.contextRunning = false;
    for (const task of this.currentTasks) {
      if (task.status === 'pending' || task.status === 'running' || task.status === 'retrying') {
        task.status = 'cancelled';
        task.finishedAt = Date.now();
      }
    }
  }

  private isBlocking(): boolean {
    const state = this.snapshotSubject.value.state;
    return state === 'booting' || state === 'warming' || state === 'error';
  }

  private publishSnapshot(
    state: BootState,
    tasks: MutableBootTask[],
    generation: number,
    error: unknown | null,
  ): void {
    if (generation !== this.sessionGeneration) {
      return;
    }
    const phaseTask = tasks.find((task) => task.status === 'running' || task.status === 'retrying');
    const phaseLabel = error
      ? 'No se pudo iniciar la aplicación'
      : phaseTask?.label ?? (state === 'ready' ? 'Aplicación lista' : state === 'warming' ? 'Preparando primera ruta' : 'Iniciando aplicación');
    this.snapshotSubject.next({
      state,
      progress: calculateBootProgress(tasks),
      phaseLabel,
      sessionGeneration: generation,
      activeUserId: this.activeUserId,
      tasks: tasks.map((task) => ({ ...task })),
      error,
    });
  }
}
